/**
 * Authentication Context
 * Manages user authentication state
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in, then refresh from server for fresh data (e.g. studioName)
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));

      // Refresh user data from server (updates studioName, role, token etc.)
      authAPI.getCurrentUser().then(response => {
        const freshUser = response.data;
        // If the server issued a fresh token (e.g. role changed), update it
        if (freshUser.token) {
          localStorage.setItem('token', freshUser.token);
          delete freshUser.token; // don't store token in user object
        }
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
      }).catch(() => {
        // Token expired or invalid — log out
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isOwner = () => user?.role === 'Owner';
  const isSuperuser = () => !!user?.isSuperuser;

  const value = {
    user,
    login,
    logout,
    isOwner,
    isSuperuser,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
